import { Attribute, Module, StoreType } from "@apis/index";
import Header from "@commons/header";
import ModuleDropdown from "@commons/modules/dropdown";
import Button from "@components/button";
import Column from "@components/column";
import Fields from "@components/fields";
import Row from "@components/row";
import Wrapper from "@components/wrapper";
import ButtonWrapper from "@components/wrapper/button-wrapper";
import { RootState } from "@store/index";
import { Form, Formik } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const StoreTypeForm = () => {
  const { state } = useLocation();

  const [initialValues, setInitialValues] = useState<any>({
    moduleId: "",
    name: "",
    icon: null,
    headerBanners: [],
    slidingBanners: [],
  });

  const fetchStoreTypes = async () => {
    await StoreType.list({
      state: "storeTypes",
    });
  };

  const fetchModule = useCallback(async () => {
    await Module.list({ state: "modules", params: { isActive: true } });
  }, []);

  const navigate = useNavigate();

  useEffect(() => {
    fetchModule();
    if (state) {
      setInitialValues({
        moduleId: state.module?.id || "",
        name: state.name || "",
        icon: state.icon ? { url: state.icon } : null,
        headerBanners: state.headerBanners || [],
        slidingBanners: state.slidingBanners || [],
      });
    } else {
      setInitialValues({
        moduleId: "",
        name: "",
        icon: null,
        headerBanners: [],
        slidingBanners: [],
      });
    }
  }, [state, fetchModule]);

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    const loadingToast = toast.loading(
      state?.id ? "Updating store type..." : "Creating store type...",
    );
    try {
      setSubmitting(true);
      const formData = new FormData();
      formData.append("moduleId", values.moduleId);
      formData.append("name", values.name);

      // Handle Icon
      if (values.icon) {
        if (values.icon.rawFile) {
          formData.append("icon", values.icon.rawFile);
        } else if (Array.isArray(values.icon) && values.icon[0]?.rawFile) {
          formData.append("icon", values.icon[0].rawFile);
        }
      }

      // Handle Header Banners
      if (values.headerBanners && Array.isArray(values.headerBanners)) {
        values.headerBanners.forEach((banner: any, idx: number) => {
          if (banner.id) {
            formData.append(`headerBanners[${idx}][id]`, banner.id);
            formData.append(`headerBanners[${idx}][bannerType]`, "header");
            if (banner.image) {
              formData.append(`headerBanners[${idx}][oldImage]`, banner.image);
            }
          }
          if (banner.rawFile) {
            formData.append(`headerBanners[${idx}][image]`, banner.rawFile);
            formData.append(`headerBanners[${idx}][bannerType]`, "header");
          }
        });
      }

      // Handle Sliding Banners
      if (values.slidingBanners && Array.isArray(values.slidingBanners)) {
        values.slidingBanners.forEach((banner: any, idx: number) => {
          if (banner.id) {
            formData.append(`slidingBanners[${idx}][id]`, banner.id);
            formData.append(`slidingBanners[${idx}][bannerType]`, "sliding");
            if (banner.image) {
              formData.append(`slidingBanners[${idx}][oldImage]`, banner.image);
            }
          }
          if (banner.rawFile) {
            formData.append(`slidingBanners[${idx}][image]`, banner.rawFile);
            formData.append(`slidingBanners[${idx}][bannerType]`, "sliding");
          }
        });
      }

      if (state?.id) {
        await StoreType.update({
          state: "storeTypes",
          id: state?.id,
          data: formData,
        }).then(() => {
          toast.update(loadingToast, {
            render: "Store type updated successfully",
            type: "success",
            isLoading: false,
            autoClose: 3000,
          });
          navigate("/admin/users/store/add-store-type");
          fetchStoreTypes();
        });
      } else {
        await StoreType.create({ state: "storeTypes", data: formData }).then(
          (res) => {
            toast.update(loadingToast, {
              render: "Store type created successfully",
              type: "success",
              isLoading: false,
              autoClose: 3000,
            });
            fetchStoreTypes();
          },
        );
      }

      resetForm();
    } catch (err: any) {
      toast.update(loadingToast, {
        render: err?.message || "Something went wrong",
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Header title="Store Type Setup" />
      <Wrapper stylewrapper={`border-radius:12px; padding:24px;`}>
        <Formik
          initialValues={initialValues}
          enableReinitialize
          onSubmit={handleSubmit}
        >
          {(formik) => (
            <Form>
              <Column>
                <Row styleRow="display:flex; gap:20px; align-items:flex-start; margin-bottom:10px">
                  <div style={{ flex: 1, width: "100%" }}>
                    <ModuleDropdown styleInput="width:100%" />
                  </div>

                  <div style={{ flex: 1, width: "100%" }}>
                    <Fields.Input
                      name="name"
                      label="Store Type Name"
                      placeholder="e.g. Grocery"
                    />
                  </div>

                  <div style={{ flex: 1, width: "100%" }}>
                    <Fields.Upload
                      name="icon"
                      label="Icon"
                      variant="banner"
                      placeholder="Upload Icon"
                      useRawFile
                      size={120}
                    />
                  </div>
                </Row>

                <Row styleRow="margin-bottom:20px; gap:20px">
                  <div style={{ flex: 1 }}>
                    <Fields.Upload
                      name="headerBanners"
                      label="Header Banners"
                      variant="banner"
                      placeholder="Upload Header Banners (Image/Video)"
                      multiple={true}
                      maxFiles={10}
                      useRawFile
                      height={150}
                      accept="image/*,video/*"
                      onRemoveImage={async (val) => {
                        if (val.id) {
                          await StoreType.removeBanner({ id: val.id });
                        }
                      }}
                      size={60}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <Fields.Upload
                      name="slidingBanners"
                      accept="image/*,video/*"
                      label="Sliding Banners"
                      variant="banner"
                      placeholder="Upload Sliding Banners"
                      multiple={true}
                      maxFiles={10}
                      useRawFile
                      height={150}
                      onRemoveImage={async (val) => {
                        if (val.id) {
                          await StoreType.removeBanner({ id: val.id });
                        }
                      }}
                      size={60}
                    />
                  </div>
                </Row>

                <ButtonWrapper>
                  <Button
                    title="Reset"
                    type="reset"
                    variant="reset"
                    disabled={formik.isSubmitting}
                  />
                  <Button
                    title={
                      formik.isSubmitting
                        ? state?.id
                          ? "Updating..."
                          : "Saving..."
                        : state?.id
                          ? "Update Store Type"
                          : "Save Store Type"
                    }
                    type="submit"
                    disabled={formik.isSubmitting}
                  />
                </ButtonWrapper>
              </Column>
            </Form>
          )}
        </Formik>
      </Wrapper>
    </>
  );
};

export default StoreTypeForm;
